import React, { useState, useMemo, useEffect } from 'react';
import {
  Plus,
  Filter,
  Users,
  FileText,
  CheckCircle,
  User,
  UserCircle,
  DollarSign,
  Briefcase,
  Package
} from 'lucide-react';
import {
  usePagedObjects,
  formatRelativeTime,
  getCategoryColor,
  type CustomObject
} from '../../services/queries/objectQueries';
import { DevModeBanner } from '../common/DevModeBanner';
import { CreateObjectModal } from './CreateObjectModal';
import { useRecentItems } from '../../hooks/useRecentItems';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import {
  StandardButton,
  StandardSearchBar,
  StandardSelect,
  StandardLoadingState,
  StandardErrorState,
  StandardEmptyState,
  StandardPagination
} from '../standard';

interface ObjectManagerHomeProps {
  onSelectObject: (objectId: string) => void;
}

/**
 * Object Manager Home
 *
 * Displays all custom objects in the system with search and filtering capabilities.
 * Users can view object details, create new objects, and manage existing ones.
 * Shows key metrics like field count, record count, and last modified date for each object.
 *
 * Data Source: objectQueries.ts (React Query with mock data)
 *
 * @component
 */
export const ObjectManagerHome: React.FC<ObjectManagerHomeProps> = ({ onSelectObject }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'core' | 'custom' | 'system'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { data: pagedObjects, isLoading, error, refetch: refetchObjects, isFetching } = usePagedObjects({
    search: searchQuery,
    category: categoryFilter,
    isActive: true // Only show active objects
  }, {
    page: currentPage,
    pageSize
  });

  const totalPages = pagedObjects?.totalPages ?? 1;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const objects = useMemo(() => pagedObjects?.items ?? [], [pagedObjects?.items]);

  // Feature flags for filtering objects in MVP mode
  const { isObjectEnabledByApiName, mvpMode } = useFeatureFlags();

  // Filter objects based on MVP feature flags
  // Object Manager is a System Admin tool — when Gobodo API is enabled,
  // show ALL objects from the graph so admins can configure everything.
  // MVP filtering only applies to mock data (no Gobodo).
  const filteredObjects = useMemo(() => {
    if (!objects) return [];

    const USE_GOBODO_API = process.env.REACT_APP_USE_GOBODO_API === 'true';

    // When Gobodo API is active, show all objects — admins need full visibility
    if (USE_GOBODO_API) return objects;

    // When not in MVP mode (mock data), show everything
    if (!mvpMode) return objects;

    // For mock data in MVP mode, apply whitelist filtering
    return objects.filter(obj => isObjectEnabledByApiName(obj.apiName));
  }, [objects, mvpMode, isObjectEnabledByApiName]);

  // Gobodo can occasionally return duplicate object rows; keep the first record
  // so React keys stay stable and Object Manager does not render duplicate cards.
  const dedupedObjects = useMemo(() => {
    const unique = new Map<string, CustomObject>();
    for (const obj of filteredObjects || []) {
      const key = String(obj.id || obj.apiName || obj.label).trim().toLowerCase();
      if (!unique.has(key)) unique.set(key, obj);
    }
    return Array.from(unique.values());
  }, [filteredObjects]);

  // Icon mapping
  const iconMap: Record<string, React.ComponentType<any>> = {
    Users,
    FileText,
    CheckCircle,
    User,
    UserCircle,
    DollarSign,
    Briefcase,
    Package
  };

  return (
    <div className="object-manager-home p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Object Manager</h1>
        <p className="text-gray-600 dark:text-gray-400">
          View and customize all objects, fields, page layouts, and relationships in your system.
        </p>
      </div>

      {/* Development Mode Banner */}
      <DevModeBanner
        dataSource="mockDataManager.ts"
        itemCount={`${dedupedObjects?.length || 0} objects on this page${mvpMode ? ' (MVP filtered)' : ''}`}
        customMessage={`Development Mode: Showing ${dedupedObjects?.length || 0} object${dedupedObjects?.length === 1 ? '' : 's'} on page ${currentPage} of ${totalPages}${mvpMode ? ' (MVP mode active - some objects hidden)' : ''}. Data persisted via mockDataManager.ts (localStorage).`}
        dismissible={true}
      />

      {/* Search and Filter Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <StandardSearchBar
              value={searchQuery}
              onChange={(value) => {
                setSearchQuery(value);
                setCurrentPage(1);
              }}
              placeholder="Search objects..."
            />
          </div>

          {/* Category Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <StandardSelect
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value as any);
                setCurrentPage(1);
              }}
              options={[
                { value: 'all', label: 'All Objects' },
                { value: 'core', label: 'Core Objects' },
                { value: 'custom', label: 'Custom Objects' },
                { value: 'system', label: 'System Objects' }
              ]}
            />
          </div>

          {/* New Object Button */}
          <StandardButton
            variant="primary"
            leftIcon={<Plus className="w-5 h-5" />}
            onClick={() => setShowCreateModal(true)}
          >
            New Object
          </StandardButton>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <StandardLoadingState message="Loading objects..." />
      )}

      {/* Error State */}
      {error && (
        <StandardErrorState
          title="Error loading objects"
          message="Please try again."
        />
      )}

      {/* Objects Grid */}
      {!isLoading && !error && (
        <>
          {/* Results Count */}
          <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
            Showing {dedupedObjects?.length || 0} of {pagedObjects?.totalRecords || 0} object{(pagedObjects?.totalRecords || 0) !== 1 ? 's' : ''}
            {isFetching && !isLoading && (
              <span className="ml-2 text-blue-600 dark:text-blue-400">Refreshing page...</span>
            )}
            {mvpMode && objects && dedupedObjects && objects.length > dedupedObjects.length && (
              <span className="ml-2 text-amber-600 dark:text-amber-400">
                ({objects.length - dedupedObjects.length} hidden in MVP mode)
              </span>
            )}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {dedupedObjects?.map((object) => (
              <ObjectCard
                key={object.id}
                object={object}
                iconMap={iconMap}
                onClick={() => onSelectObject(object.id)}
              />
            ))}
          </div>

          {/* Empty State */}
          {dedupedObjects?.length === 0 && (
            <StandardEmptyState
              icon={<Package className="w-16 h-16" />}
              title="No objects found"
              message={mvpMode ? "No objects match your criteria. Some objects may be hidden in MVP mode." : "Try adjusting your search or filter criteria"}
              actionLabel="Clear filters"
              onAction={() => {
                setSearchQuery('');
                setCategoryFilter('all');
                setCurrentPage(1);
              }}
            />
          )}

          {dedupedObjects.length > 0 && (
            <div className="mt-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4">
              <StandardPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={pagedObjects?.totalRecords || 0}
                pageSize={pageSize}
                pageSizeOptions={[25, 50, 100]}
                onPageChange={setCurrentPage}
                onPageSizeChange={(nextPageSize) => {
                  setPageSize(nextPageSize);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}
        </>
      )}

      {/* Create Object Modal */}
      {showCreateModal && (
        <CreateObjectModal 
          onClose={() => setShowCreateModal(false)}
          onObjectCreated={() => {
            // Refetch the objects list when a new object is successfully created
            refetchObjects();
          }}
        />
      )}
    </div>
  );
};

// ============================================
// OBJECT CARD COMPONENT
// ============================================

interface ObjectCardProps {
  object: CustomObject;
  iconMap: Record<string, React.ComponentType<any>>;
  onClick: () => void;
}

const ObjectCard: React.FC<ObjectCardProps> = ({ object, iconMap, onClick }) => {
  const IconComponent = iconMap[object.icon] || Package;
  const { addRecentItem } = useRecentItems();

  const handleClick = () => {
    console.log('[ObjectCard] Clicked on object:', object.label);
    // Track object view in Recent Items
    addRecentItem({
      id: `object-${object.id}`,
      title: object.label,
      subtitle: `${object.apiName} • ${object.category} object`,
      path: '/admin?section=object-manager',
      type: 'object',
      icon: 'Package'
    });

    // Call the original onClick handler
    onClick();
  };

  return (
    <button
      onClick={handleClick}
      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 hover:border-purple-300 dark:hover:border-purple-500 hover:shadow-md transition-all text-left group focus:outline-none focus:ring-2 focus:ring-purple-500"
    >
      {/* Header with Icon and Category */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center group-hover:bg-purple-200 dark:group-hover:bg-purple-800/50 transition-colors">
            <IconComponent className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">{object.label}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">{object.apiName}</p>
          </div>
        </div>
        <span className={`px-2 py-1 rounded text-xs font-medium ${getCategoryColor(object.category)}`}>
          {object.category}
        </span>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
        {object.description}
      </p>

      {/* Stats */}
      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 pt-3 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-4">
          <span>{object.fieldCount} fields</span>
          <span>{object.recordCount} records</span>
        </div>
        <span>{formatRelativeTime(object.lastModified)}</span>
      </div>
    </button>
  );
};

export default ObjectManagerHome;
